import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import NotesIcon from "@mui/icons-material/Notes";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import LinkIcon from "@mui/icons-material/Link";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import DownloadIcon from "@mui/icons-material/Download";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import PageTitle from "../../widgets/PageTitle";
import BackBtn from "../../widgets/BackBtn";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import SectionLabel from "../../widgets/SectionLabel";
import MuPb from "../../widgets/MuPb";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";

const FLOW_JSON_VERSION = "7.3";
const TEXT_TYPES = ["TextHeading", "TextSubheading", "TextCaption", "TextBody"];
const TEXT_LIMITS = {
  TextHeading: 80,
  TextSubheading: 80,
  TextBody: 4096,
  TextCaption: 409,
};
const INPUT_TYPES = [
  "text",
  "number",
  "email",
  "password",
  "passcode",
  "phone",
];
const OPTION_TYPES = ["CheckboxGroup", "RadioButtonsGroup", "Dropdown"];
const NAMED_TYPES = [
  "TextInput",
  "TextArea",
  "CheckboxGroup",
  "RadioButtonsGroup",
  "Dropdown",
  "DatePicker",
  "CalendarPicker",
  "OptIn",
];
const VISIBLE_TYPES = [
  "Text",
  "RichText",
  "TextInput",
  "TextArea",
  "CheckboxGroup",
  "RadioButtonsGroup",
  "Dropdown",
  "DatePicker",
  "CalendarPicker",
  "OptIn",
  "EmbeddedLink",
];
const ENABLED_TYPES = [
  "TextInput",
  "TextArea",
  "CheckboxGroup",
  "RadioButtonsGroup",
  "Dropdown",
  "DatePicker",
  "CalendarPicker",
  "Footer",
];

export const AVAILABLE_FIELDS = [
  {
    id: "Text",
    type: "Text",
    label: "Text",
    category: "Display",
    icon: <FormatColorTextIcon fontSize="small" />,
  },
  {
    id: "RichText",
    type: "RichText",
    label: "Rich Text",
    category: "Display",
    icon: <NotesIcon fontSize="small" />,
  },
  {
    id: "TextInput",
    type: "TextInput",
    label: "Text Input",
    category: "Input",
    icon: <TextFieldsIcon fontSize="small" />,
  },
  {
    id: "TextArea",
    type: "TextArea",
    label: "Text Area",
    category: "Input",
    icon: <NotesIcon fontSize="small" />,
  },
  {
    id: "CheckboxGroup",
    type: "CheckboxGroup",
    label: "Checkbox Group",
    category: "Selection",
    icon: <CheckBoxOutlineBlankOutlinedIcon fontSize="small" />,
  },
  {
    id: "RadioButtonsGroup",
    type: "RadioButtonsGroup",
    label: "Radio Group",
    category: "Selection",
    icon: <RadioButtonUncheckedOutlinedIcon fontSize="small" />,
  },
  {
    id: "Dropdown",
    type: "Dropdown",
    label: "Dropdown",
    category: "Selection",
    icon: <ArrowDropDownCircleOutlinedIcon fontSize="small" />,
  },
  {
    id: "OptIn",
    type: "OptIn",
    label: "Opt-in / Consent",
    category: "Selection",
    icon: <FactCheckOutlinedIcon fontSize="small" />,
  },
  {
    id: "DatePicker",
    type: "DatePicker",
    label: "Date Picker",
    category: "Date",
    icon: <CalendarTodayIcon fontSize="small" />,
  },
  {
    id: "CalendarPicker",
    type: "CalendarPicker",
    label: "Calendar Picker",
    category: "Date",
    icon: <DateRangeOutlinedIcon fontSize="small" />,
  },
  {
    id: "EmbeddedLink",
    type: "EmbeddedLink",
    label: "Embedded Link",
    category: "Action",
    icon: <LinkIcon fontSize="small" />,
  },
  {
    id: "Footer",
    type: "Footer",
    label: "Footer Button",
    category: "Action",
    icon: <SquareFootIcon fontSize="small" />,
  },
];

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? {}));

const omit = (obj, keys) => {
  const result = { ...(obj || {}) };
  keys.forEach((key) => delete result[key]);
  return result;
};

const safeString = (value, fallback = "") =>
  typeof value === "string" ? value : fallback;

const sanitizeFieldName = (value, fallback = "field") => {
  let normalized = safeString(value)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

  if (!normalized) normalized = fallback;
  if (/^[0-9]/.test(normalized)) normalized = `field_${normalized}`;
  return normalized.slice(0, 50);
};

const sanitizeScreenId = (value, fallback = "SCREEN") => {
  let normalized = safeString(value)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

  if (!normalized) normalized = fallback;
  if (/^[0-9]/.test(normalized)) normalized = `SCREEN_${normalized}`;
  return normalized.slice(0, 50);
};

const makeUniqueValue = (baseValue, usedValues, separator = "_") => {
  const used = new Set(usedValues.filter(Boolean));
  if (!used.has(baseValue)) return baseValue;
  let index = 2;
  while (used.has(`${baseValue}${separator}${index}`)) index += 1;
  return `${baseValue}${separator}${index}`;
};

const isValidJsonObject = (value) => {
  try {
    const parsed = JSON.parse(value || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed);
  } catch (_) {
    return false;
  }
};

const parseJsonObject = (value, fallback = {}) => {
  try {
    const parsed = JSON.parse(value || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : fallback;
  } catch (_) {
    return fallback;
  }
};

const normalizeOption = (option, index = 0) => {
  if (typeof option === "string") {
    return {
      editorId: uuidv4(),
      id: sanitizeFieldName(option, `option_${index + 1}`),
      title: option,
      description: "",
      metadata: "",
      enabled: true,
      extra: {},
    };
  }

  const source = option || {};
  return {
    editorId: uuidv4(),
    id: safeString(source.id, `option_${index + 1}`),
    title: safeString(source.title, `Option ${index + 1}`),
    description: safeString(source.description),
    metadata: safeString(source.metadata),
    enabled: source.enabled !== false,
    extra: omit(source, ["id", "title", "description", "metadata", "enabled"]),
  };
};

const normalizeAction = (action) => {
  const source = action || {};
  const payload = source.payload || {};
  return {
    name: safeString(source.name, "auto"),
    nextScreenId: safeString(source.next?.name),
    url: safeString(source.url),
    payloadText: JSON.stringify(payload, null, 2),
    extra: omit(source, ["name", "next", "url", "payload"]),
  };
};

const normalizeComponent = (component) => {
  const source = deepClone(component || {});
  const base = {
    editorId: uuidv4(),
    type: source.type || "RawComponent",
  };

  if (TEXT_TYPES.includes(source.type)) {
    return {
      ...base,
      type: "Text",
      subType: source.type,
      text: safeString(source.text),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      markdown: Boolean(source.markdown),
      fontWeight: safeString(source["font-weight"], "normal"),
      strikethrough: Boolean(source.strikethrough),
      extra: omit(source, [
        "type",
        "text",
        "visible",
        "markdown",
        "font-weight",
        "strikethrough",
      ]),
    };
  }

  if (source.type === "RichText") {
    return {
      ...base,
      text: Array.isArray(source.text)
        ? source.text.join("\n")
        : safeString(source.text),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      extra: omit(source, ["type", "text", "visible"]),
    };
  }

  if (source.type === "TextInput") {
    return {
      ...base,
      name: safeString(source.name, "text_input"),
      label: safeString(source.label, "Text input"),
      inputType: safeString(source["input-type"], "text"),
      required: Boolean(source.required),
      helperText: safeString(source["helper-text"]),
      pattern: safeString(source.pattern),
      minChars: safeString(source["min-chars"]),
      maxChars: safeString(source["max-chars"]),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      extra: omit(source, [
        "type",
        "name",
        "label",
        "input-type",
        "required",
        "helper-text",
        "pattern",
        "min-chars",
        "max-chars",
        "visible",
        "enabled",
      ]),
    };
  }

  if (source.type === "TextArea") {
    return {
      ...base,
      name: safeString(source.name, "text_area"),
      label: safeString(source.label, "Message"),
      required: Boolean(source.required),
      helperText: safeString(source["helper-text"]),
      maxLength: safeString(source["max-length"], "600"),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      extra: omit(source, [
        "type",
        "name",
        "label",
        "required",
        "helper-text",
        "max-length",
        "visible",
        "enabled",
      ]),
    };
  }

  if (OPTION_TYPES.includes(source.type)) {
    return {
      ...base,
      name: safeString(
        source.name,
        sanitizeFieldName(source.label, "selection")
      ),
      label: safeString(source.label, "Select an option"),
      description: safeString(source.description),
      required: Boolean(source.required),
      options: Array.isArray(source["data-source"])
        ? source["data-source"].map(normalizeOption)
        : [],
      minSelected:
        source["min-selected-items"] === undefined
          ? ""
          : String(source["min-selected-items"]),
      maxSelected:
        source["max-selected-items"] === undefined
          ? ""
          : String(source["max-selected-items"]),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      extra: omit(source, [
        "type",
        "name",
        "label",
        "description",
        "required",
        "data-source",
        "min-selected-items",
        "max-selected-items",
        "visible",
        "enabled",
      ]),
    };
  }

  if (source.type === "OptIn") {
    const action = normalizeAction(source["on-click-action"]);
    return {
      ...base,
      name: safeString(source.name, "consent"),
      label: safeString(source.label, "I agree"),
      required: Boolean(source.required),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      action,
      extra: omit(source, [
        "type",
        "name",
        "label",
        "required",
        "visible",
        "on-click-action",
      ]),
    };
  }

  if (source.type === "DatePicker") {
    return {
      ...base,
      name: safeString(source.name, "date"),
      label: safeString(source.label, "Select a date"),
      required: Boolean(source.required),
      helperText: safeString(source["helper-text"]),
      minDate: safeString(source["min-date"]),
      maxDate: safeString(source["max-date"]),
      unavailableDates: Array.isArray(source["unavailable-dates"])
        ? source["unavailable-dates"].join(", ")
        : "",
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      extra: omit(source, [
        "type",
        "name",
        "label",
        "required",
        "helper-text",
        "min-date",
        "max-date",
        "unavailable-dates",
        "visible",
        "enabled",
      ]),
    };
  }

  if (source.type === "CalendarPicker") {
    const rangeMode = source.mode === "range";
    const label = source.label;
    const helper = source["helper-text"];
    const required = source.required;
    return {
      ...base,
      name: safeString(source.name, "calendar"),
      mode: rangeMode ? "range" : "single",
      label: rangeMode
        ? safeString(label?.["start-date"], "Start date")
        : safeString(label, "Select a date"),
      endLabel: rangeMode
        ? safeString(label?.["end-date"], "End date")
        : "End date",
      title: safeString(source.title),
      description: safeString(source.description),
      helperText: rangeMode
        ? safeString(helper?.["start-date"])
        : safeString(helper),
      endHelperText: rangeMode ? safeString(helper?.["end-date"]) : "",
      required: rangeMode
        ? Boolean(required?.["start-date"])
        : Boolean(required),
      endRequired: rangeMode ? Boolean(required?.["end-date"]) : false,
      minDate: safeString(source["min-date"]),
      maxDate: safeString(source["max-date"]),
      minDays:
        source["min-days"] === undefined ? "" : String(source["min-days"]),
      maxDays:
        source["max-days"] === undefined ? "" : String(source["max-days"]),
      unavailableDates: Array.isArray(source["unavailable-dates"])
        ? source["unavailable-dates"].join(", ")
        : "",
      includeDays: Array.isArray(source["include-days"])
        ? source["include-days"]
        : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      extra: omit(source, [
        "type",
        "name",
        "mode",
        "label",
        "title",
        "description",
        "helper-text",
        "required",
        "min-date",
        "max-date",
        "min-days",
        "max-days",
        "unavailable-dates",
        "include-days",
        "visible",
        "enabled",
      ]),
    };
  }

  if (source.type === "EmbeddedLink") {
    return {
      ...base,
      text: safeString(source.text, "Open link"),
      visible: source.visible !== false,
      visibleExpression:
        typeof source.visible === "string" ? source.visible : "",
      action: normalizeAction(source["on-click-action"]),
      extra: omit(source, ["type", "text", "visible", "on-click-action"]),
    };
  }

  if (source.type === "Footer") {
    return {
      ...base,
      label: safeString(source.label, "Continue"),
      leftCaption: safeString(source["left-caption"]),
      centerCaption: safeString(source["center-caption"]),
      rightCaption: safeString(source["right-caption"]),
      enabled: source.enabled !== false,
      enabledExpression:
        typeof source.enabled === "string" ? source.enabled : "",
      action: normalizeAction(source["on-click-action"]),
      extra: omit(source, [
        "type",
        "label",
        "left-caption",
        "center-caption",
        "right-caption",
        "enabled",
        "on-click-action",
      ]),
    };
  }

  return {
    editorId: uuidv4(),
    type: "RawComponent",
    originalType: source.type || "Unknown",
    rawJson: JSON.stringify(source, null, 2),
  };
};

const normalizeScreen = (screen, index) => {
  const source = deepClone(screen || {});
  const layout = source.layout || { type: "SingleColumnLayout", children: [] };
  const layoutChildren = Array.isArray(layout.children) ? layout.children : [];
  const formIndex = layoutChildren.findIndex((item) => item?.type === "Form");
  const form = formIndex >= 0 ? layoutChildren[formIndex] : null;
  const editableChildren = form?.children || layoutChildren;

  return {
    editorId: uuidv4(),
    id: safeString(source.id, `SCREEN_${index + 1}`),
    title: safeString(source.title, `Screen ${index + 1}`),
    terminal: Boolean(source.terminal),
    success: source.success,
    data: deepClone(source.data || {}),
    usesFormWrapper: Boolean(form),
    formName: safeString(form?.name, "form"),
    formExtra: form ? omit(form, ["type", "name", "children"]) : {},
    outsideFormChildren: form
      ? layoutChildren.filter((_, childIndex) => childIndex !== formIndex)
      : [],
    layoutExtra: omit(layout, ["type", "children"]),
    extra: omit(source, [
      "id",
      "title",
      "terminal",
      "success",
      "data",
      "layout",
    ]),
    fields: editableChildren.map(normalizeComponent),
  };
};

const normalizeFlowForEditor = (flowJson) => {
  const source = deepClone(flowJson || {});
  return {
    rootExtra: omit(source, ["version", "screens"]),
    version: safeString(source.version, FLOW_JSON_VERSION),
    screens: Array.isArray(source.screens)
      ? source.screens.map(normalizeScreen)
      : [],
  };
};

const getDefaultField = (type, existingFields = []) => {
  const usedNames = existingFields.map((field) => field.name).filter(Boolean);
  const uniqueName = (base) =>
    makeUniqueValue(sanitizeFieldName(base), usedNames, "_");

  switch (type) {
    case "Text":
      return {
        editorId: uuidv4(),
        type: "Text",
        subType: "TextHeading",
        text: "Heading",
        visible: true,
        visibleExpression: "",
        markdown: false,
        fontWeight: "normal",
        strikethrough: false,
        extra: {},
      };
    case "RichText":
      return {
        editorId: uuidv4(),
        type: "RichText",
        text: "**Rich text**",
        visible: true,
        visibleExpression: "",
        extra: {},
      };
    case "TextInput":
      return {
        editorId: uuidv4(),
        type: "TextInput",
        name: uniqueName("text_input"),
        label: "Text input",
        inputType: "text",
        required: true,
        helperText: "",
        pattern: "",
        minChars: "",
        maxChars: "",
        visible: true,
        visibleExpression: "",
        enabled: true,
        enabledExpression: "",
        extra: {},
      };
    case "TextArea":
      return {
        editorId: uuidv4(),
        type: "TextArea",
        name: uniqueName("message"),
        label: "Message",
        required: false,
        helperText: "",
        maxLength: "600",
        visible: true,
        visibleExpression: "",
        enabled: true,
        enabledExpression: "",
        extra: {},
      };
    case "CheckboxGroup":
    case "RadioButtonsGroup":
    case "Dropdown":
      return {
        editorId: uuidv4(),
        type,
        name: uniqueName(
          type === "CheckboxGroup"
            ? "choices"
            : type === "RadioButtonsGroup"
            ? "choice"
            : "selection"
        ),
        label:
          type === "CheckboxGroup"
            ? "Choose all that apply"
            : "Select an option",
        description: "",
        required: true,
        options: [
          normalizeOption({ id: "option_1", title: "Option 1" }, 0),
          normalizeOption({ id: "option_2", title: "Option 2" }, 1),
        ],
        minSelected: "",
        maxSelected: "",
        visible: true,
        visibleExpression: "",
        enabled: true,
        enabledExpression: "",
        extra: {},
      };
    case "OptIn":
      return {
        editorId: uuidv4(),
        type: "OptIn",
        name: uniqueName("consent"),
        label: "I agree to the terms",
        required: true,
        visible: true,
        visibleExpression: "",
        action: {
          name: "open_url",
          url: "https://example.com/terms",
          nextScreenId: "",
          payloadText: "{}",
          extra: {},
        },
        extra: {},
      };
    case "DatePicker":
      return {
        editorId: uuidv4(),
        type: "DatePicker",
        name: uniqueName("date"),
        label: "Select a date",
        required: true,
        helperText: "",
        minDate: "",
        maxDate: "",
        unavailableDates: "",
        visible: true,
        visibleExpression: "",
        enabled: true,
        enabledExpression: "",
        extra: {},
      };
    case "CalendarPicker":
      return {
        editorId: uuidv4(),
        type: "CalendarPicker",
        name: uniqueName("calendar"),
        mode: "single",
        label: "Select a date",
        endLabel: "End date",
        title: "",
        description: "",
        helperText: "",
        endHelperText: "",
        required: true,
        endRequired: true,
        minDate: "",
        maxDate: "",
        minDays: "",
        maxDays: "",
        unavailableDates: "",
        includeDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        visible: true,
        visibleExpression: "",
        enabled: true,
        enabledExpression: "",
        extra: {},
      };
    case "EmbeddedLink":
      return {
        editorId: uuidv4(),
        type: "EmbeddedLink",
        text: "Read more",
        visible: true,
        visibleExpression: "",
        action: {
          name: "open_url",
          url: "https://example.com",
          nextScreenId: "",
          payloadText: "{}",
          extra: {},
        },
        extra: {},
      };
    case "Footer":
      return {
        editorId: uuidv4(),
        type: "Footer",
        label: "Continue",
        leftCaption: "",
        centerCaption: "",
        rightCaption: "",
        enabled: true,
        enabledExpression: "",
        action: {
          name: "auto",
          nextScreenId: "",
          url: "",
          payloadText: "{}",
          extra: {},
        },
        extra: {},
      };
    default:
      return null;
  }
};

const renderVisibleValue = (field) =>
  field.visibleExpression?.trim() || field.visible !== false;

const renderEnabledValue = (field) =>
  field.enabledExpression?.trim() || field.enabled !== false;

const optionToFlow = (option) => {
  const result = {
    ...(option.extra || {}),
    id: safeString(option.id),
    title: safeString(option.title),
  };
  if (option.description?.trim())
    result.description = option.description.trim();
  if (option.metadata?.trim()) result.metadata = option.metadata.trim();
  if (option.enabled === false) result.enabled = false;
  return result;
};

const actionToFlow = (field, screenIndex, screens, isLastScreen) => {
  const action = field.action || {};
  const payload = parseJsonObject(action.payloadText, {});
  const actionName = action.name || "auto";

  if (actionName === "auto") {
    if (isLastScreen) {
      const completePayload = {};
      screens.forEach((screen, sourceScreenIndex) => {
        screen.fields.forEach((sourceField) => {
          if (!NAMED_TYPES.includes(sourceField.type) || !sourceField.name)
            return;
          const key = sourceField.name;
          if (sourceScreenIndex === screenIndex) {
            completePayload[key] = `\${form.${sourceField.name}}`;
          } else {
            completePayload[
              key
            ] = `\${screen.${screen.id}.form.${sourceField.name}}`;
          }
        });
      });
      return {
        name: "complete",
        payload: completePayload,
      };
    }

    return {
      name: "navigate",
      next: {
        type: "screen",
        name: screens[screenIndex + 1]?.id || "",
      },
    };
  }

  if (actionName === "complete") {
    return {
      ...(action.extra || {}),
      name: "complete",
      ...(Object.keys(payload).length ? { payload } : {}),
    };
  }

  if (actionName === "navigate") {
    return {
      ...(action.extra || {}),
      name: "navigate",
      next: {
        type: "screen",
        name:
          action.nextScreenId ||
          screens[screenIndex + 1]?.id ||
          screens[screenIndex]?.id ||
          "",
      },
      ...(Object.keys(payload).length ? { payload } : {}),
    };
  }

  if (actionName === "data_exchange") {
    return {
      ...(action.extra || {}),
      name: "data_exchange",
      ...(Object.keys(payload).length ? { payload } : {}),
    };
  }

  if (actionName === "open_url") {
    return {
      ...(action.extra || {}),
      name: "open_url",
      url: action.url,
    };
  }

  return {
    ...(action.extra || {}),
    name: actionName,
    ...(Object.keys(payload).length ? { payload } : {}),
  };
};

const fieldToFlowComponent = (field, screenIndex, screens, isLastScreen) => {
  const visible = renderVisibleValue(field);
  const enabled = renderEnabledValue(field);

  if (field.type === "Text") {
    const component = {
      ...(field.extra || {}),
      type: field.subType || "TextBody",
      text: field.text,
    };
    if (visible !== true) component.visible = visible;
    if (["TextBody", "TextCaption"].includes(component.type)) {
      if (field.markdown) component.markdown = true;
      if (field.fontWeight && field.fontWeight !== "normal") {
        component["font-weight"] = field.fontWeight;
      }
      if (field.strikethrough) component.strikethrough = true;
    }
    return component;
  }

  if (field.type === "RichText") {
    const component = {
      ...(field.extra || {}),
      type: "RichText",
      text: field.text,
    };
    if (visible !== true) component.visible = visible;
    return component;
  }

  if (field.type === "TextInput") {
    const component = {
      ...(field.extra || {}),
      type: "TextInput",
      name: field.name,
      label: field.label,
      "input-type": field.inputType || "text",
      required: Boolean(field.required),
    };
    if (field.helperText?.trim())
      component["helper-text"] = field.helperText.trim();
    if (field.pattern?.trim()) component.pattern = field.pattern.trim();
    if (field.minChars !== "") component["min-chars"] = String(field.minChars);
    if (field.maxChars !== "") component["max-chars"] = String(field.maxChars);
    if (visible !== true) component.visible = visible;
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (field.type === "TextArea") {
    const component = {
      ...(field.extra || {}),
      type: "TextArea",
      name: field.name,
      label: field.label,
      required: Boolean(field.required),
    };
    if (field.helperText?.trim())
      component["helper-text"] = field.helperText.trim();
    if (field.maxLength !== "")
      component["max-length"] = String(field.maxLength);
    if (visible !== true) component.visible = visible;
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (OPTION_TYPES.includes(field.type)) {
    const component = {
      ...(field.extra || {}),
      type: field.type,
      name: field.name,
      label: field.label,
      "data-source": field.options.map(optionToFlow),
      required: Boolean(field.required),
    };
    if (field.description?.trim())
      component.description = field.description.trim();
    if (field.type === "CheckboxGroup") {
      if (field.minSelected !== "") {
        component["min-selected-items"] = Number(field.minSelected);
      }
      if (field.maxSelected !== "") {
        component["max-selected-items"] = Number(field.maxSelected);
      }
    }
    if (visible !== true) component.visible = visible;
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (field.type === "OptIn") {
    const component = {
      ...(field.extra || {}),
      type: "OptIn",
      name: field.name,
      label: field.label,
      required: Boolean(field.required),
    };
    if (visible !== true) component.visible = visible;
    if (field.action?.name && field.action.name !== "none") {
      component["on-click-action"] = actionToFlow(
        field,
        screenIndex,
        screens,
        isLastScreen
      );
    }
    return component;
  }

  if (field.type === "DatePicker") {
    const component = {
      ...(field.extra || {}),
      type: "DatePicker",
      name: field.name,
      label: field.label,
      required: Boolean(field.required),
    };
    if (field.helperText?.trim())
      component["helper-text"] = field.helperText.trim();
    if (field.minDate) component["min-date"] = field.minDate;
    if (field.maxDate) component["max-date"] = field.maxDate;
    const unavailable = field.unavailableDates
      ?.split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (unavailable?.length) component["unavailable-dates"] = unavailable;
    if (visible !== true) component.visible = visible;
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (field.type === "CalendarPicker") {
    const component = {
      ...(field.extra || {}),
      type: "CalendarPicker",
      name: field.name,
      mode: field.mode || "single",
    };

    if (field.mode === "range") {
      component.label = {
        "start-date": field.label,
        "end-date": field.endLabel,
      };
      component.required = {
        "start-date": Boolean(field.required),
        "end-date": Boolean(field.endRequired),
      };
      if (field.helperText || field.endHelperText) {
        component["helper-text"] = {
          "start-date": field.helperText || "",
          "end-date": field.endHelperText || "",
        };
      }
      if (field.title?.trim()) component.title = field.title.trim();
      if (field.description?.trim())
        component.description = field.description.trim();
      if (field.minDays !== "") component["min-days"] = Number(field.minDays);
      if (field.maxDays !== "") component["max-days"] = Number(field.maxDays);
    } else {
      component.label = field.label;
      component.required = Boolean(field.required);
      if (field.helperText?.trim())
        component["helper-text"] = field.helperText.trim();
    }

    if (field.minDate) component["min-date"] = field.minDate;
    if (field.maxDate) component["max-date"] = field.maxDate;
    const unavailable = field.unavailableDates
      ?.split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (unavailable?.length) component["unavailable-dates"] = unavailable;
    if (Array.isArray(field.includeDays) && field.includeDays.length < 7) {
      component["include-days"] = field.includeDays;
    }
    if (visible !== true) component.visible = visible;
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (field.type === "EmbeddedLink") {
    const component = {
      ...(field.extra || {}),
      type: "EmbeddedLink",
      text: field.text,
      "on-click-action": actionToFlow(
        field,
        screenIndex,
        screens,
        isLastScreen
      ),
    };
    if (visible !== true) component.visible = visible;
    return component;
  }

  if (field.type === "Footer") {
    const component = {
      ...(field.extra || {}),
      type: "Footer",
      label: field.label,
      "on-click-action": actionToFlow(
        field,
        screenIndex,
        screens,
        isLastScreen
      ),
    };
    if (field.centerCaption?.trim()) {
      component["center-caption"] = field.centerCaption.trim();
    } else {
      if (field.leftCaption?.trim()) {
        component["left-caption"] = field.leftCaption.trim();
      }
      if (field.rightCaption?.trim()) {
        component["right-caption"] = field.rightCaption.trim();
      }
    }
    if (enabled !== true) component.enabled = enabled;
    return component;
  }

  if (field.type === "RawComponent") {
    try {
      return JSON.parse(field.rawJson);
    } catch (_) {
      return {
        type: "TextCaption",
        text: `Invalid raw component: ${field.originalType || "Unknown"}`,
      };
    }
  }

  return null;
};

const buildFlowJson = (draft) => {
  const screens = draft.screens || [];
  const builtScreens = screens.map((screen, screenIndex) => {
    const isLastScreen = screenIndex === screens.length - 1;
    const footerFields = screen.fields.filter(
      (field) => field.type === "Footer"
    );
    const nonFooterFields = screen.fields.filter(
      (field) => field.type !== "Footer"
    );
    const orderedFields = [...nonFooterFields, ...footerFields];
    const children = orderedFields
      .map((field) =>
        fieldToFlowComponent(field, screenIndex, screens, isLastScreen)
      )
      .filter(Boolean);

    let layoutChildren = children;
    if (screen.usesFormWrapper) {
      const form = {
        ...(screen.formExtra || {}),
        type: "Form",
        name: screen.formName || "form",
        children,
      };
      layoutChildren = [...(screen.outsideFormChildren || []), form];
    }

    const result = {
      ...(screen.extra || {}),
      id: screen.id,
      title: screen.title,
      terminal: isLastScreen,
      data: deepClone(screen.data || {}),
      layout: {
        ...(screen.layoutExtra || {}),
        type: "SingleColumnLayout",
        children: layoutChildren,
      },
    };

    if (screen.success !== undefined) result.success = screen.success;
    return result;
  });

  const root = {
    ...(draft.rootExtra || {}),
    version: FLOW_JSON_VERSION,
    screens: builtScreens,
  };

  const hasDataExchange = builtScreens.some((screen) => {
    const inspect = (children = []) =>
      children.some((child) => {
        if (child?.type === "Form") return inspect(child.children || []);
        return child?.["on-click-action"]?.name === "data_exchange";
      });
    return inspect(screen.layout?.children || []);
  });

  if (hasDataExchange && !root.data_api_version) {
    root.data_api_version = "3.0";
  }

  if (hasDataExchange && !root.routing_model) {
    root.routing_model = {};
    screens.forEach((screen, screenIndex) => {
      const footer = screen.fields.find((field) => field.type === "Footer");
      const actionName = footer?.action?.name || "auto";
      let nextIds = [];

      if (actionName === "navigate" && footer?.action?.nextScreenId) {
        nextIds = [footer.action.nextScreenId];
      } else if (
        ["auto", "data_exchange"].includes(actionName) &&
        screenIndex < screens.length - 1
      ) {
        nextIds = [screens[screenIndex + 1].id];
      }

      root.routing_model[screen.id] = nextIds;
    });
  }

  return root;
};

const validateFlowDraft = (draft) => {
  const errors = [];
  const warnings = [];
  const screens = draft.screens || [];

  if (!screens.length) {
    errors.push("Flow must contain at least one screen.");
    return { errors, warnings };
  }

  const screenIds = screens.map((screen) => screen.id);
  const duplicateScreenIds = screenIds.filter(
    (id, index) => screenIds.indexOf(id) !== index
  );
  if (duplicateScreenIds.length) {
    errors.push(
      `Duplicate screen IDs: ${[...new Set(duplicateScreenIds)].join(", ")}`
    );
  }

  const allNames = [];

  screens.forEach((screen, screenIndex) => {
    const screenLabel =
      screen.title || screen.id || `Screen ${screenIndex + 1}`;

    if (!screen.id?.trim())
      errors.push(`${screenLabel}: screen ID is required.`);
    if (!/^[A-Za-z0-9_]+$/.test(screen.id || "")) {
      errors.push(
        `${screenLabel}: screen ID can only use letters, numbers and underscores.`
      );
    }
    if (!screen.title?.trim())
      errors.push(`${screen.id}: screen title is required.`);
    if ((screen.title || "").length > 30) {
      errors.push(
        `${screenLabel}: screen title must be 30 characters or less.`
      );
    }
    if (screen.fields.length > 50) {
      errors.push(`${screenLabel}: maximum 50 components per screen.`);
    }

    const footerCount = screen.fields.filter(
      (field) => field.type === "Footer"
    ).length;
    if (footerCount === 0) {
      errors.push(`${screenLabel}: add a Footer button.`);
    } else if (footerCount > 1) {
      errors.push(`${screenLabel}: only one Footer is allowed.`);
    }

    const optInCount = screen.fields.filter(
      (field) => field.type === "OptIn"
    ).length;
    if (optInCount > 5)
      errors.push(`${screenLabel}: maximum 5 OptIn components.`);

    const embeddedLinkCount = screen.fields.filter(
      (field) => field.type === "EmbeddedLink"
    ).length;
    if (embeddedLinkCount > 2) {
      errors.push(`${screenLabel}: maximum 2 EmbeddedLink components.`);
    }

    const screenNames = [];

    screen.fields.forEach((field, fieldIndex) => {
      const fieldLabel = field.label || field.text || field.type;
      const prefix = `${screenLabel} → ${
        fieldLabel || `component ${fieldIndex + 1}`
      }`;

      if (NAMED_TYPES.includes(field.type)) {
        if (!field.name?.trim())
          errors.push(`${prefix}: field name is required.`);
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(field.name || "")) {
          errors.push(
            `${prefix}: field name must start with a letter/underscore and contain only letters, numbers and underscores.`
          );
        }
        if (screenNames.includes(field.name)) {
          errors.push(`${screenLabel}: duplicate field name "${field.name}".`);
        }
        screenNames.push(field.name);
        allNames.push({ name: field.name, screen: screen.id });
      }

      if (field.type === "Text") {
        const textValue = field.text || "";
        const limit = TEXT_LIMITS[field.subType] || 4096;
        if (!textValue.trim()) errors.push(`${prefix}: text cannot be empty.`);
        if (textValue.length > limit) {
          errors.push(`${prefix}: text exceeds ${limit} characters.`);
        }
      }

      if (field.type === "RichText" && !field.text?.trim()) {
        errors.push(`${prefix}: rich text cannot be empty.`);
      }

      if (["TextInput", "TextArea"].includes(field.type)) {
        if (!field.label?.trim()) errors.push(`${prefix}: label is required.`);
        if ((field.label || "").length > 20) {
          errors.push(`${prefix}: label must be 20 characters or less.`);
        }
      }

      if (field.type === "TextInput") {
        if (field.helperText?.length > 80) {
          errors.push(`${prefix}: helper text must be 80 characters or less.`);
        }
        if (field.pattern?.trim() && !field.helperText?.trim()) {
          errors.push(
            `${prefix}: helper text is required when a regex pattern is used.`
          );
        }
        const min = field.minChars === "" ? null : Number(field.minChars);
        const max = field.maxChars === "" ? null : Number(field.maxChars);
        if (min !== null && (!Number.isInteger(min) || min < 0)) {
          errors.push(
            `${prefix}: min characters must be a non-negative integer.`
          );
        }
        if (max !== null && (!Number.isInteger(max) || max < 1)) {
          errors.push(`${prefix}: max characters must be a positive integer.`);
        }
        if (min !== null && max !== null && min > max) {
          errors.push(
            `${prefix}: min characters cannot exceed max characters.`
          );
        }
      }

      if (field.type === "TextArea") {
        if (field.helperText?.length > 80) {
          errors.push(`${prefix}: helper text must be 80 characters or less.`);
        }
        const maxLength = Number(field.maxLength || 600);
        if (!Number.isInteger(maxLength) || maxLength < 1 || maxLength > 600) {
          errors.push(`${prefix}: max length must be between 1 and 600.`);
        }
      }

      if (OPTION_TYPES.includes(field.type)) {
        const limit = field.type === "Dropdown" ? 200 : 20;
        const labelLimit = field.type === "Dropdown" ? 20 : 30;
        if (!field.label?.trim()) errors.push(`${prefix}: label is required.`);
        if ((field.label || "").length > labelLimit) {
          errors.push(
            `${prefix}: label must be ${labelLimit} characters or less.`
          );
        }
        if (!field.options.length)
          errors.push(`${prefix}: at least one option is required.`);
        if (field.options.length > limit) {
          errors.push(`${prefix}: maximum ${limit} options.`);
        }

        const optionIds = [];
        field.options.forEach((option, optionIndex) => {
          if (!option.id?.trim()) {
            errors.push(`${prefix}: option ${optionIndex + 1} needs an ID.`);
          }
          if (!option.title?.trim()) {
            errors.push(`${prefix}: option ${optionIndex + 1} needs a title.`);
          }
          if ((option.title || "").length > 30) {
            errors.push(
              `${prefix}: option ${
                optionIndex + 1
              } title exceeds 30 characters.`
            );
          }
          if ((option.description || "").length > 300) {
            errors.push(
              `${prefix}: option ${
                optionIndex + 1
              } description exceeds 300 characters.`
            );
          }
          if ((option.metadata || "").length > 20) {
            errors.push(
              `${prefix}: option ${
                optionIndex + 1
              } metadata exceeds 20 characters.`
            );
          }
          if (optionIds.includes(option.id)) {
            errors.push(`${prefix}: duplicate option ID "${option.id}".`);
          }
          optionIds.push(option.id);
        });

        if (field.type === "CheckboxGroup") {
          const min =
            field.minSelected === "" ? null : Number(field.minSelected);
          const max =
            field.maxSelected === "" ? null : Number(field.maxSelected);
          if (min !== null && (!Number.isInteger(min) || min < 0)) {
            errors.push(
              `${prefix}: minimum selected items must be a non-negative integer.`
            );
          }
          if (max !== null && (!Number.isInteger(max) || max < 1)) {
            errors.push(
              `${prefix}: maximum selected items must be a positive integer.`
            );
          }
          if (min !== null && max !== null && min > max) {
            errors.push(
              `${prefix}: minimum selected items cannot exceed maximum.`
            );
          }
          if (max !== null && max > field.options.length) {
            errors.push(
              `${prefix}: maximum selected items cannot exceed option count.`
            );
          }
        }

        if (field.type === "RadioButtonsGroup" && field.options.length > 8) {
          warnings.push(
            `${prefix}: ${field.options.length} radio options may be easier to use as a Dropdown.`
          );
        }
      }

      if (field.type === "OptIn") {
        if (!field.label?.trim()) errors.push(`${prefix}: label is required.`);
        if ((field.label || "").length > 120) {
          errors.push(`${prefix}: label must be 120 characters or less.`);
        }
        if (field.action?.name === "open_url" && !field.action.url?.trim()) {
          errors.push(`${prefix}: URL is required for open_url.`);
        }
      }

      if (["DatePicker", "CalendarPicker"].includes(field.type)) {
        if (!field.label?.trim()) errors.push(`${prefix}: label is required.`);
        if ((field.label || "").length > 40) {
          errors.push(`${prefix}: label must be 40 characters or less.`);
        }
        if (field.helperText?.length > 80) {
          errors.push(`${prefix}: helper text must be 80 characters or less.`);
        }
        if (field.minDate && field.maxDate && field.minDate > field.maxDate) {
          errors.push(`${prefix}: min date cannot be after max date.`);
        }
      }

      if (field.type === "CalendarPicker" && field.mode === "range") {
        if (!field.endLabel?.trim())
          errors.push(`${prefix}: end-date label is required.`);
        if ((field.endLabel || "").length > 40) {
          errors.push(
            `${prefix}: end-date label must be 40 characters or less.`
          );
        }
        const minDays = field.minDays === "" ? null : Number(field.minDays);
        const maxDays = field.maxDays === "" ? null : Number(field.maxDays);
        if (minDays !== null && (!Number.isInteger(minDays) || minDays < 0)) {
          errors.push(`${prefix}: min days must be a non-negative integer.`);
        }
        if (maxDays !== null && (!Number.isInteger(maxDays) || maxDays < 1)) {
          errors.push(`${prefix}: max days must be a positive integer.`);
        }
        if (minDays !== null && maxDays !== null && minDays > maxDays) {
          errors.push(`${prefix}: min days cannot exceed max days.`);
        }
      }

      if (field.type === "EmbeddedLink") {
        if (!field.text?.trim()) errors.push(`${prefix}: text is required.`);
        if ((field.text || "").length > 25) {
          errors.push(`${prefix}: text must be 25 characters or less.`);
        }
        if (field.action?.name === "open_url" && !field.action.url?.trim()) {
          errors.push(`${prefix}: URL is required for open_url.`);
        }
      }

      if (field.type === "Footer") {
        if (!field.label?.trim())
          errors.push(`${prefix}: button label is required.`);
        if ((field.label || "").length > 35) {
          errors.push(`${prefix}: Footer label must be 35 characters or less.`);
        }
        [field.leftCaption, field.centerCaption, field.rightCaption]
          .filter(Boolean)
          .forEach((caption) => {
            if (caption.length > 15) {
              errors.push(
                `${prefix}: Footer captions must be 15 characters or less.`
              );
            }
          });
        if (field.centerCaption && (field.leftCaption || field.rightCaption)) {
          errors.push(
            `${prefix}: center caption cannot be combined with left/right captions.`
          );
        }
        if (!isValidJsonObject(field.action?.payloadText || "{}")) {
          errors.push(`${prefix}: action payload must be a valid JSON object.`);
        }
        if (field.action?.name === "navigate") {
          if (!field.action.nextScreenId) {
            errors.push(`${prefix}: choose a target screen.`);
          } else if (!screenIds.includes(field.action.nextScreenId)) {
            errors.push(
              `${prefix}: target screen "${field.action.nextScreenId}" does not exist.`
            );
          }
        }
      }

      if (field.type === "RawComponent") {
        try {
          const parsed = JSON.parse(field.rawJson);
          if (!parsed?.type)
            errors.push(`${prefix}: raw component must contain a type.`);
        } catch (_) {
          errors.push(`${prefix}: raw component JSON is invalid.`);
        }
      }
    });

    const inputCount = screen.fields.filter((field) =>
      NAMED_TYPES.includes(field.type)
    ).length;
    if (inputCount > 8) {
      warnings.push(
        `${screenLabel}: ${inputCount} input components is a lot for one mobile screen; consider splitting the task.`
      );
    }
  });

  const duplicateAcrossScreens = allNames.filter(
    (item, index) =>
      allNames.findIndex((other) => other.name === item.name) !== index
  );
  if (duplicateAcrossScreens.length) {
    warnings.push(
      `Repeated field names across screens: ${[
        ...new Set(duplicateAcrossScreens.map((item) => item.name)),
      ].join(", ")}. Auto-complete payload keys may overwrite each other.`
    );
  }

  return { errors, warnings };
};

const getTextVariant = (type) => {
  switch (type) {
    case "TextHeading":
      return "h5";
    case "TextSubheading":
      return "h6";
    case "TextCaption":
      return "caption";
    default:
      return "body1";
  }
};

const FieldShell = ({ children }) => (
  <Box sx={{ width: "100%", pointerEvents: "none" }}>{children}</Box>
);

const FlowForms = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/api/get-flow-asset/${id}`
  );

  const [flowDraft, setFlowDraft] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(0);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [screenDialog, setScreenDialog] = useState({
    open: false,
    mode: "add",
    title: "",
    id: "",
    dataText: "{}",
    refreshOnBack: false,
  });
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [bulkOptions, setBulkOptions] = useState("");
  const [componentSearch, setComponentSearch] = useState("");

  useEffect(() => {
    if (data?.json) {
      const normalized = normalizeFlowForEditor(data.json);
      setFlowDraft(normalized);
      setCurrentScreen((prev) =>
        normalized.screens.length
          ? Math.min(prev, normalized.screens.length - 1)
          : 0
      );
      setSelectedFieldId(null);
      setIsDrawerOpen(false);
    }
  }, [data]);

  const screens = flowDraft?.screens || [];
  const activeScreen = screens[currentScreen] || null;
  const formFields = activeScreen?.fields || [];

  const validation = useMemo(
    () =>
      flowDraft ? validateFlowDraft(flowDraft) : { errors: [], warnings: [] },
    [flowDraft]
  );

  const generatedJson = useMemo(
    () => (flowDraft ? buildFlowJson(flowDraft) : {}),
    [flowDraft]
  );

  const generatedJsonText = useMemo(
    () => JSON.stringify(generatedJson, null, 2),
    [generatedJson]
  );

  const generatedSizeKb = useMemo(
    () => new Blob([generatedJsonText]).size / 1024,
    [generatedJsonText]
  );

  const filteredAvailableFields = useMemo(() => {
    const query = componentSearch.trim().toLowerCase();
    if (!query) return AVAILABLE_FIELDS;
    return AVAILABLE_FIELDS.filter((field) =>
      `${field.label} ${field.category} ${field.type}`
        .toLowerCase()
        .includes(query)
    );
  }, [componentSearch]);

  const componentCategories = useMemo(
    () => [...new Set(filteredAvailableFields.map((field) => field.category))],
    [filteredAvailableFields]
  );

  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  if (isLoading || !flowDraft) return <MuPb />;

  const updateActiveScreen = (updater) => {
    setFlowDraft((prev) => {
      const next = deepClone(prev);
      const screen = next.screens[currentScreen];
      if (!screen) return prev;
      const updated = typeof updater === "function" ? updater(screen) : updater;
      next.screens[currentScreen] = updated;
      return next;
    });
  };

  const updateFields = (updater) => {
    updateActiveScreen((screen) => ({
      ...screen,
      fields: typeof updater === "function" ? updater(screen.fields) : updater,
    }));
  };

  const updateField = (fieldId, patch) => {
    updateFields((fields) =>
      fields.map((field) =>
        field.editorId === fieldId
          ? {
              ...field,
              ...(typeof patch === "function" ? patch(field) : patch),
            }
          : field
      )
    );
  };

  const handleFieldEdit = (fieldId) => {
    setSelectedFieldId(fieldId);
    setBulkOptions("");
    setIsDrawerOpen(true);
  };

  const handleFieldDelete = (fieldId) => {
    updateFields((fields) =>
      fields.filter((field) => field.editorId !== fieldId)
    );
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
      setIsDrawerOpen(false);
    }
  };

  const duplicateField = (fieldId) => {
    updateFields((fields) => {
      const index = fields.findIndex((field) => field.editorId === fieldId);
      if (index < 0) return fields;
      const cloned = deepClone(fields[index]);
      cloned.editorId = uuidv4();
      if (cloned.name) {
        cloned.name = makeUniqueValue(
          cloned.name,
          fields.map((field) => field.name).filter(Boolean),
          "_"
        );
      }
      if (Array.isArray(cloned.options)) {
        cloned.options = cloned.options.map((option) => ({
          ...option,
          editorId: uuidv4(),
        }));
      }
      return [
        ...fields.slice(0, index + 1),
        cloned,
        ...fields.slice(index + 1),
      ];
    });
  };

  const addFieldFromPalette = (
    sourceField,
    destinationIndex = formFields.length
  ) => {
    const newField = getDefaultField(sourceField.type, formFields);
    if (!newField) return;

    if (
      newField.type === "Footer" &&
      formFields.some((field) => field.type === "Footer")
    ) {
      toast.info("Only one Footer is allowed per screen.");
      return;
    }

    const nextFields = [...formFields];
    nextFields.splice(destinationIndex, 0, newField);
    updateFields(nextFields);

    setSelectedFieldId(newField.editorId);
    setBulkOptions("");
    setIsDrawerOpen(true);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || destination.droppableId !== "formFields") return;

    if (source.droppableId === "availableFields") {
      const sourceField = AVAILABLE_FIELDS[source.index];
      addFieldFromPalette(sourceField, destination.index);
      return;
    }

    const nextFields = [...formFields];
    const [moved] = nextFields.splice(source.index, 1);
    nextFields.splice(destination.index, 0, moved);
    updateFields(nextFields);
  };

  const openAddScreen = () => {
    setScreenDialog({
      open: true,
      mode: "add",
      title: "",
      id: "",
      dataText: "{}",
      refreshOnBack: false,
    });
  };

  const openEditScreen = () => {
    if (!activeScreen) return;
    setScreenDialog({
      open: true,
      mode: "edit",
      title: activeScreen.title,
      id: activeScreen.id,
      dataText: JSON.stringify(activeScreen.data || {}, null, 2),
      refreshOnBack: Boolean(activeScreen.extra?.refresh_on_back),
    });
  };

  const closeScreenDialog = () => {
    setScreenDialog({
      open: false,
      mode: "add",
      title: "",
      id: "",
      dataText: "{}",
      refreshOnBack: false,
    });
  };

  const saveScreenDialog = () => {
    const title = screenDialog.title.trim();
    const requestedId = sanitizeScreenId(screenDialog.id || title);
    if (!title) {
      toast.error("Screen title is required.");
      return;
    }
    if (!isValidJsonObject(screenDialog.dataText || "{}")) {
      toast.error("Screen data schema must be a valid JSON object.");
      return;
    }
    const screenData = parseJsonObject(screenDialog.dataText || "{}", {});

    if (screenDialog.mode === "add") {
      const uniqueId = makeUniqueValue(
        requestedId,
        screens.map((screen) => screen.id),
        "_"
      );
      const newScreen = normalizeScreen(
        {
          id: uniqueId,
          title,
          terminal: true,
          data: screenData,
          ...(screenDialog.refreshOnBack ? { refresh_on_back: true } : {}),
          layout: {
            type: "SingleColumnLayout",
            children: [
              {
                type: "TextHeading",
                text: title,
              },
              {
                type: "Footer",
                label: "Complete",
                "on-click-action": { name: "complete", payload: {} },
              },
            ],
          },
        },
        screens.length
      );

      setFlowDraft((prev) => ({
        ...prev,
        screens: [...prev.screens, newScreen],
      }));
      setCurrentScreen(screens.length);
      closeScreenDialog();
      return;
    }

    const duplicate = screens.some(
      (screen, index) => index !== currentScreen && screen.id === requestedId
    );
    if (duplicate) {
      toast.error("Another screen already uses that ID.");
      return;
    }

    const oldScreenId = activeScreen.id;
    setFlowDraft((prev) => {
      const next = deepClone(prev);
      next.screens[currentScreen] = {
        ...next.screens[currentScreen],
        title,
        id: requestedId,
        data: screenData,
        extra: {
          ...(next.screens[currentScreen].extra || {}),
          ...(screenDialog.refreshOnBack
            ? { refresh_on_back: true }
            : (() => {
                const extra = { ...(next.screens[currentScreen].extra || {}) };
                delete extra.refresh_on_back;
                return extra;
              })()),
        },
      };

      if (oldScreenId !== requestedId) {
        next.screens = next.screens.map((screen) => ({
          ...screen,
          fields: screen.fields.map((field) => {
            if (field.action?.nextScreenId === oldScreenId) {
              return {
                ...field,
                action: {
                  ...field.action,
                  nextScreenId: requestedId,
                },
              };
            }
            return field;
          }),
        }));
      }

      return next;
    });
    closeScreenDialog();
  };

  const duplicateScreen = () => {
    if (!activeScreen) return;
    const clone = deepClone(activeScreen);
    clone.editorId = uuidv4();
    clone.id = makeUniqueValue(
      `${sanitizeScreenId(activeScreen.id)}_COPY`,
      screens.map((screen) => screen.id),
      "_"
    );
    clone.title = `${activeScreen.title} Copy`.slice(0, 60);
    clone.fields = clone.fields.map((field) => ({
      ...field,
      editorId: uuidv4(),
      options: Array.isArray(field.options)
        ? field.options.map((option) => ({ ...option, editorId: uuidv4() }))
        : field.options,
    }));

    setFlowDraft((prev) => {
      const nextScreens = [...prev.screens];
      nextScreens.splice(currentScreen + 1, 0, clone);
      return { ...prev, screens: nextScreens };
    });
    setCurrentScreen(currentScreen + 1);
  };

  const deleteScreen = () => {
    if (screens.length <= 1) {
      toast.error("A Flow must keep at least one screen.");
      return;
    }
    if (!window.confirm(`Delete screen "${activeScreen.title}"?`)) return;

    setFlowDraft((prev) => ({
      ...prev,
      screens: prev.screens.filter((_, index) => index !== currentScreen),
    }));
    setCurrentScreen((prev) => Math.max(0, prev - 1));
    setSelectedFieldId(null);
    setIsDrawerOpen(false);
  };

  const moveScreen = (direction) => {
    const target = currentScreen + direction;
    if (target < 0 || target >= screens.length) return;
    setFlowDraft((prev) => {
      const next = deepClone(prev);
      const [moved] = next.screens.splice(currentScreen, 1);
      next.screens.splice(target, 0, moved);
      return next;
    });
    setCurrentScreen(target);
  };

  const handleSaveDraft = async () => {
    if (validation.errors.length) {
      setValidationDialogOpen(true);
      return;
    }

    try {
      setSavingDraft(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/update-flow-json/${id}`,
        generatedJson,
        getHeader()
      );
      toast.success(response.data?.message || "Flow draft saved.");
      await refetch?.();
    } catch (saveError) {
      toastError(saveError);
    } finally {
      setSavingDraft(false);
    }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(generatedJsonText);
      toast.success("Flow JSON copied.");
    } catch (_) {
      toast.error("Could not copy JSON from this browser.");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([generatedJsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${data?.detail?.name || "whatsapp-flow"}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const renderFormElement = (field) => {
    switch (field.type) {
      case "Text":
        return (
          <Typography
            variant={getTextVariant(field.subType)}
            sx={{
              fontWeight:
                field.fontWeight === "bold" ||
                field.fontWeight === "bold_italic"
                  ? 700
                  : undefined,
              fontStyle:
                field.fontWeight === "italic" ||
                field.fontWeight === "bold_italic"
                  ? "italic"
                  : undefined,
              textDecoration: field.strikethrough ? "line-through" : undefined,
              whiteSpace: "pre-wrap",
            }}
          >
            {field.text || "Empty text"}
          </Typography>
        );

      case "RichText":
        return (
          <Box>
            <Chip label="RichText / Markdown" size="small" sx={{ mb: 1 }} />
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {field.text || "Empty rich text"}
            </Typography>
          </Box>
        );

      case "TextInput":
        return (
          <FieldShell>
            <TextField
              fullWidth
              size="small"
              label={field.label}
              type={field.inputType === "password" ? "password" : "text"}
              required={field.required}
              helperText={field.helperText}
            />
          </FieldShell>
        );

      case "TextArea":
        return (
          <FieldShell>
            <TextField
              fullWidth
              size="small"
              label={field.label}
              multiline
              minRows={3}
              required={field.required}
              helperText={field.helperText}
            />
          </FieldShell>
        );

      case "CheckboxGroup":
        return (
          <FieldShell>
            <FormControl component="fieldset">
              <FormLabel>{field.label}</FormLabel>
              {field.description && (
                <FormHelperText sx={{ ml: 0 }}>
                  {field.description}
                </FormHelperText>
              )}
              <FormGroup>
                {field.options.slice(0, 5).map((option) => (
                  <FormControlLabel
                    key={option.editorId}
                    control={<Checkbox size="small" />}
                    label={option.title}
                  />
                ))}
              </FormGroup>
              {field.options.length > 5 && (
                <Typography variant="caption" color="text.secondary">
                  +{field.options.length - 5} more options
                </Typography>
              )}
            </FormControl>
          </FieldShell>
        );

      case "RadioButtonsGroup":
        return (
          <FieldShell>
            <FormControl>
              <FormLabel>{field.label}</FormLabel>
              {field.description && (
                <FormHelperText sx={{ ml: 0 }}>
                  {field.description}
                </FormHelperText>
              )}
              <RadioGroup>
                {field.options.slice(0, 5).map((option) => (
                  <FormControlLabel
                    key={option.editorId}
                    value={option.id}
                    control={<Radio size="small" />}
                    label={option.title}
                  />
                ))}
              </RadioGroup>
              {field.options.length > 5 && (
                <Typography variant="caption" color="text.secondary">
                  +{field.options.length - 5} more options
                </Typography>
              )}
            </FormControl>
          </FieldShell>
        );

      case "Dropdown":
        return (
          <FieldShell>
            <FormControl size="small" fullWidth>
              <InputLabel>{field.label}</InputLabel>
              <Select value="" label={field.label}>
                {field.options.slice(0, 10).map((option) => (
                  <MenuItem key={option.editorId} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldShell>
        );

      case "OptIn":
        return (
          <FieldShell>
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={`${field.label}${field.required ? " *" : ""}`}
            />
          </FieldShell>
        );

      case "DatePicker":
        return (
          <FieldShell>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={field.label}
              InputLabelProps={{ shrink: true }}
              helperText={field.helperText}
            />
          </FieldShell>
        );

      case "CalendarPicker":
        return (
          <FieldShell>
            <Stack spacing={1}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={field.label}
                InputLabelProps={{ shrink: true }}
                helperText={field.helperText}
              />
              {field.mode === "range" && (
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={field.endLabel}
                  InputLabelProps={{ shrink: true }}
                  helperText={field.endHelperText}
                />
              )}
            </Stack>
          </FieldShell>
        );

      case "EmbeddedLink":
        return (
          <Button size="small" variant="text" startIcon={<LinkIcon />}>
            {field.text}
          </Button>
        );

      case "Footer":
        return (
          <FieldShell>
            <Button fullWidth variant="contained" size="small">
              {field.label}
            </Button>
            {(field.leftCaption ||
              field.centerCaption ||
              field.rightCaption) && (
              <Stack direction="row" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption">{field.leftCaption}</Typography>
                <Typography variant="caption">{field.centerCaption}</Typography>
                <Typography variant="caption">{field.rightCaption}</Typography>
              </Stack>
            )}
          </FieldShell>
        );

      case "RawComponent":
        return (
          <Alert severity="info" icon={<CodeIcon />}>
            Preserved advanced component: <strong>{field.originalType}</strong>
          </Alert>
        );

      default:
        return <Typography variant="body2">{field.type}</Typography>;
    }
  };

  const selectedField = formFields.find(
    (field) => field.editorId === selectedFieldId
  );

  const renderCommonStateControls = (field) => (
    <Stack spacing={2} mt={2}>
      {VISIBLE_TYPES.includes(field.type) && (
        <>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={field.visible !== false}
                onChange={(e) =>
                  updateField(field.editorId, { visible: e.target.checked })
                }
              />
            }
            label="Visible by default"
          />
          <TextField
            size="small"
            label="Visibility expression (optional)"
            value={field.visibleExpression || ""}
            onChange={(e) =>
              updateField(field.editorId, { visibleExpression: e.target.value })
            }
            helperText={
              "Example: ${form.has_company} == true. If set, it overrides the switch."
            }
            fullWidth
          />
        </>
      )}

      {ENABLED_TYPES.includes(field.type) && (
        <>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={field.enabled !== false}
                onChange={(e) =>
                  updateField(field.editorId, { enabled: e.target.checked })
                }
              />
            }
            label="Enabled by default"
          />
          <TextField
            size="small"
            label="Enabled expression (optional)"
            value={field.enabledExpression || ""}
            onChange={(e) =>
              updateField(field.editorId, { enabledExpression: e.target.value })
            }
            helperText="Use a dynamic boolean expression only when needed."
            fullWidth
          />
        </>
      )}
    </Stack>
  );

  const renderActionEditor = (field, allowedActions) => {
    const action = field.action || {};
    const setAction = (patch) =>
      updateField(field.editorId, {
        action: { ...action, ...patch },
      });

    return (
      <Stack spacing={2} mt={2}>
        <FormControl size="small" fullWidth>
          <InputLabel>Action</InputLabel>
          <Select
            label="Action"
            value={action.name || allowedActions[0]}
            onChange={(e) => setAction({ name: e.target.value })}
          >
            {allowedActions.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {action.name === "navigate" && (
          <FormControl size="small" fullWidth>
            <InputLabel>Target screen</InputLabel>
            <Select
              label="Target screen"
              value={action.nextScreenId || ""}
              onChange={(e) => setAction({ nextScreenId: e.target.value })}
            >
              {screens.map((screen) => (
                <MenuItem key={screen.editorId} value={screen.id}>
                  {screen.title} ({screen.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {action.name === "open_url" && (
          <TextField
            size="small"
            label="URL"
            value={action.url || ""}
            onChange={(e) => setAction({ url: e.target.value })}
            fullWidth
          />
        )}

        {["navigate", "complete", "data_exchange"].includes(action.name) && (
          <TextField
            size="small"
            label="Payload JSON"
            value={action.payloadText || "{}"}
            onChange={(e) => setAction({ payloadText: e.target.value })}
            fullWidth
            multiline
            minRows={4}
            error={!isValidJsonObject(action.payloadText || "{}")}
            helperText="JSON object only. Dynamic references such as ${form.email} can be used as string values."
          />
        )}
      </Stack>
    );
  };

  const renderOptionsEditor = (field) => {
    const optionLimit = field.type === "Dropdown" ? 200 : 20;

    const updateOption = (optionId, patch) => {
      updateField(field.editorId, {
        options: field.options.map((option) =>
          option.editorId === optionId ? { ...option, ...patch } : option
        ),
      });
    };

    const addOption = () => {
      if (field.options.length >= optionLimit) {
        toast.error(`Maximum ${optionLimit} options for ${field.type}.`);
        return;
      }
      const nextIndex = field.options.length + 1;
      const usedIds = field.options.map((option) => option.id);
      const id = makeUniqueValue(`option_${nextIndex}`, usedIds, "_");
      updateField(field.editorId, {
        options: [
          ...field.options,
          normalizeOption({ id, title: `Option ${nextIndex}` }, nextIndex - 1),
        ],
      });
    };

    const removeOption = (optionId) => {
      updateField(field.editorId, {
        options: field.options.filter((option) => option.editorId !== optionId),
      });
    };

    const addBulkOptions = () => {
      const lines = bulkOptions
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return;

      const remaining = optionLimit - field.options.length;
      const toAdd = lines.slice(0, Math.max(0, remaining));
      const usedIds = [...field.options.map((option) => option.id)];
      const additions = toAdd.map((title, index) => {
        const baseId = sanitizeFieldName(
          title,
          `option_${field.options.length + index + 1}`
        );
        const id = makeUniqueValue(baseId, usedIds, "_");
        usedIds.push(id);
        return normalizeOption({ id, title }, field.options.length + index);
      });

      updateField(field.editorId, {
        options: [...field.options, ...additions],
      });
      setBulkOptions("");

      if (lines.length > toAdd.length) {
        toast.info(
          `Added ${toAdd.length}; ${field.type} limit is ${optionLimit}.`
        );
      }
    };

    return (
      <Box mt={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <SectionLabel>Options</SectionLabel>
          <Chip
            size="small"
            label={`${field.options.length}/${optionLimit}`}
            color={field.options.length > optionLimit ? "error" : "default"}
          />
        </Stack>

        <Stack spacing={2} mt={1}>
          {field.options.map((option, index) => (
            <Paper key={option.editorId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label={`Option ${index + 1}`}
                    value={option.title}
                    inputProps={{ maxLength: 30 }}
                    onChange={(e) => {
                      const title = e.target.value;
                      const previousAutoId = sanitizeFieldName(
                        option.title,
                        `option_${index + 1}`
                      );
                      const shouldUpdateId = option.id === previousAutoId;
                      updateOption(option.editorId, {
                        title,
                        ...(shouldUpdateId
                          ? {
                              id: sanitizeFieldName(
                                title,
                                `option_${index + 1}`
                              ),
                            }
                          : {}),
                      });
                    }}
                    fullWidth
                  />
                  <Tooltip title="Delete option">
                    <IconButton
                      size="small"
                      onClick={() => removeOption(option.editorId)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <TextField
                  size="small"
                  label="Option ID"
                  value={option.id}
                  onChange={(e) =>
                    updateOption(option.editorId, {
                      id: sanitizeFieldName(
                        e.target.value,
                        `option_${index + 1}`
                      ),
                    })
                  }
                  helperText="Stable value returned by WhatsApp; keep it independent from display text."
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Description (optional)"
                  value={option.description || ""}
                  inputProps={{ maxLength: 300 }}
                  onChange={(e) =>
                    updateOption(option.editorId, {
                      description: e.target.value,
                    })
                  }
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Metadata (optional)"
                  value={option.metadata || ""}
                  inputProps={{ maxLength: 20 }}
                  onChange={(e) =>
                    updateOption(option.editorId, { metadata: e.target.value })
                  }
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={option.enabled !== false}
                      onChange={(e) =>
                        updateOption(option.editorId, {
                          enabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Option enabled"
                />
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Stack direction="row" justifyContent="flex-end" mt={1.5}>
          <Button size="small" startIcon={<AddIcon />} onClick={addOption}>
            Add option
          </Button>
        </Stack>

        <Box mt={2}>
          <TextField
            size="small"
            label="Bulk add options"
            placeholder={"Small\nMedium\nLarge"}
            value={bulkOptions}
            onChange={(e) => setBulkOptions(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            helperText="One title per line. IDs are generated automatically and remain editable."
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            variant="outlined"
            onClick={addBulkOptions}
            disabled={!bulkOptions.trim()}
          >
            Add lines
          </Button>
        </Box>
      </Box>
    );
  };

  const renderEditingPanel = () => {
    const field = selectedField;
    if (!field) return null;

    const update = (patch) => updateField(field.editorId, patch);

    if (field.type === "RawComponent") {
      return (
        <Box p={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This component is preserved because the visual editor does not model
            it yet. You can still edit its raw JSON without losing it.
          </Alert>
          <TextField
            label={`${field.originalType} JSON`}
            value={field.rawJson}
            onChange={(e) => update({ rawJson: e.target.value })}
            fullWidth
            multiline
            minRows={20}
            error={(() => {
              try {
                JSON.parse(field.rawJson);
                return false;
              } catch (_) {
                return true;
              }
            })()}
          />
        </Box>
      );
    }

    return (
      <Box p={2}>
        <Stack direction="row" spacing={1} mb={2} alignItems="center">
          <Chip label={field.type} size="small" />
          {field.subType && (
            <Chip label={field.subType} size="small" variant="outlined" />
          )}
        </Stack>

        {NAMED_TYPES.includes(field.type) && (
          <TextField
            size="small"
            label="Field name / response key"
            value={field.name || ""}
            onChange={(e) =>
              update({ name: sanitizeFieldName(e.target.value) })
            }
            fullWidth
            helperText="Stable key used in form references and the completion payload. Do not derive this from the label after users start using the Flow."
            sx={{ mb: 2 }}
          />
        )}

        {field.type === "Text" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Text"
              value={field.text}
              onChange={(e) => update({ text: e.target.value })}
              multiline={field.subType === "TextBody"}
              minRows={field.subType === "TextBody" ? 3 : 1}
              inputProps={{ maxLength: TEXT_LIMITS[field.subType] }}
              helperText={`${(field.text || "").length}/${
                TEXT_LIMITS[field.subType]
              }`}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Text type</InputLabel>
              <Select
                value={field.subType}
                label="Text type"
                onChange={(e) => update({ subType: e.target.value })}
              >
                {TEXT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {["TextBody", "TextCaption"].includes(field.subType) && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Font weight</InputLabel>
                  <Select
                    label="Font weight"
                    value={field.fontWeight || "normal"}
                    onChange={(e) => update({ fontWeight: e.target.value })}
                  >
                    {[
                      ["normal", "Normal"],
                      ["bold", "Bold"],
                      ["italic", "Italic"],
                      ["bold_italic", "Bold + italic"],
                    ].map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(field.markdown)}
                      onChange={(e) => update({ markdown: e.target.checked })}
                    />
                  }
                  label="Enable markdown"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(field.strikethrough)}
                      onChange={(e) =>
                        update({ strikethrough: e.target.checked })
                      }
                    />
                  }
                  label="Strikethrough"
                />
              </>
            )}
          </Stack>
        )}

        {field.type === "RichText" && (
          <TextField
            size="small"
            label="Markdown content"
            value={field.text}
            onChange={(e) => update({ text: e.target.value })}
            multiline
            minRows={12}
            fullWidth
            helperText="Useful for terms, instructions, summaries and long formatted content."
          />
        )}

        {field.type === "TextInput" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Label"
              value={field.label}
              inputProps={{ maxLength: 20 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
              helperText={`${field.label.length}/20`}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Input type</InputLabel>
              <Select
                value={field.inputType}
                label="Input type"
                onChange={(e) => update({ inputType: e.target.value })}
              >
                {INPUT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Helper text"
              value={field.helperText}
              inputProps={{ maxLength: 80 }}
              onChange={(e) => update({ helperText: e.target.value })}
              fullWidth
              helperText={`${field.helperText.length}/80`}
            />
            <TextField
              size="small"
              label="Validation regex pattern"
              value={field.pattern}
              onChange={(e) => update({ pattern: e.target.value })}
              fullWidth
              helperText="When a pattern is used, helper text should explain the expected format."
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                type="number"
                label="Min chars"
                value={field.minChars}
                onChange={(e) => update({ minChars: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Max chars"
                value={field.maxChars}
                onChange={(e) => update({ maxChars: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(field.required)}
                  onChange={(e) => update({ required: e.target.checked })}
                />
              }
              label="Required"
            />
          </Stack>
        )}

        {field.type === "TextArea" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Label"
              value={field.label}
              inputProps={{ maxLength: 20 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Helper text"
              value={field.helperText}
              inputProps={{ maxLength: 80 }}
              onChange={(e) => update({ helperText: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Max length"
              value={field.maxLength}
              inputProps={{ min: 1, max: 600 }}
              onChange={(e) => update({ maxLength: e.target.value })}
              fullWidth
              helperText="WhatsApp TextArea supports up to 600 characters."
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(field.required)}
                  onChange={(e) => update({ required: e.target.checked })}
                />
              }
              label="Required"
            />
          </Stack>
        )}

        {OPTION_TYPES.includes(field.type) && (
          <>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Label"
                value={field.label}
                inputProps={{ maxLength: field.type === "Dropdown" ? 20 : 30 }}
                onChange={(e) => update({ label: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Description (optional)"
                value={field.description || ""}
                inputProps={{ maxLength: 300 }}
                onChange={(e) => update({ description: e.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(field.required)}
                    onChange={(e) => update({ required: e.target.checked })}
                  />
                }
                label="Required"
              />
              {field.type === "CheckboxGroup" && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    label="Min selected"
                    value={field.minSelected}
                    onChange={(e) => update({ minSelected: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Max selected"
                    value={field.maxSelected}
                    onChange={(e) => update({ maxSelected: e.target.value })}
                    fullWidth
                  />
                </Stack>
              )}
            </Stack>
            {renderOptionsEditor(field)}
          </>
        )}

        {field.type === "OptIn" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Consent text"
              value={field.label}
              inputProps={{ maxLength: 120 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(field.required)}
                  onChange={(e) => update({ required: e.target.checked })}
                />
              }
              label="Required"
            />
            <SectionLabel>Read more action</SectionLabel>
            {renderActionEditor(field, [
              { value: "none", label: "None" },
              { value: "open_url", label: "Open URL" },
              { value: "navigate", label: "Navigate" },
              { value: "data_exchange", label: "Data exchange" },
            ])}
          </Stack>
        )}

        {field.type === "DatePicker" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Label"
              value={field.label}
              inputProps={{ maxLength: 40 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Helper text"
              value={field.helperText}
              inputProps={{ maxLength: 80 }}
              onChange={(e) => update({ helperText: e.target.value })}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                type="date"
                label="Minimum date"
                InputLabelProps={{ shrink: true }}
                value={field.minDate}
                onChange={(e) => update({ minDate: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="Maximum date"
                InputLabelProps={{ shrink: true }}
                value={field.maxDate}
                onChange={(e) => update({ maxDate: e.target.value })}
                fullWidth
              />
            </Stack>
            <TextField
              size="small"
              label="Unavailable dates"
              value={field.unavailableDates}
              onChange={(e) => update({ unavailableDates: e.target.value })}
              fullWidth
              helperText="Comma-separated YYYY-MM-DD values."
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(field.required)}
                  onChange={(e) => update({ required: e.target.checked })}
                />
              }
              label="Required"
            />
          </Stack>
        )}

        {field.type === "CalendarPicker" && (
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Mode</InputLabel>
              <Select
                value={field.mode}
                label="Mode"
                onChange={(e) => update({ mode: e.target.value })}
              >
                <MenuItem value="single">Single date</MenuItem>
                <MenuItem value="range">Date range</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={field.mode === "range" ? "Start-date label" : "Label"}
              value={field.label}
              inputProps={{ maxLength: 40 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
            />
            {field.mode === "range" && (
              <>
                <TextField
                  size="small"
                  label="End-date label"
                  value={field.endLabel}
                  inputProps={{ maxLength: 40 }}
                  onChange={(e) => update({ endLabel: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Calendar title"
                  value={field.title}
                  inputProps={{ maxLength: 80 }}
                  onChange={(e) => update({ title: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Description"
                  value={field.description}
                  inputProps={{ maxLength: 300 }}
                  onChange={(e) => update({ description: e.target.value })}
                  fullWidth
                />
              </>
            )}
            <TextField
              size="small"
              label={
                field.mode === "range"
                  ? "Start-date helper text"
                  : "Helper text"
              }
              value={field.helperText}
              inputProps={{ maxLength: 80 }}
              onChange={(e) => update({ helperText: e.target.value })}
              fullWidth
            />
            {field.mode === "range" && (
              <TextField
                size="small"
                label="End-date helper text"
                value={field.endHelperText}
                inputProps={{ maxLength: 80 }}
                onChange={(e) => update({ endHelperText: e.target.value })}
                fullWidth
              />
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                type="date"
                label="Minimum date"
                InputLabelProps={{ shrink: true }}
                value={field.minDate}
                onChange={(e) => update({ minDate: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="Maximum date"
                InputLabelProps={{ shrink: true }}
                value={field.maxDate}
                onChange={(e) => update({ maxDate: e.target.value })}
                fullWidth
              />
            </Stack>
            {field.mode === "range" && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  label="Min days"
                  value={field.minDays}
                  onChange={(e) => update({ minDays: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max days"
                  value={field.maxDays}
                  onChange={(e) => update({ maxDays: e.target.value })}
                  fullWidth
                />
              </Stack>
            )}
            <TextField
              size="small"
              label="Unavailable dates"
              value={field.unavailableDates}
              onChange={(e) => update({ unavailableDates: e.target.value })}
              fullWidth
              helperText="Comma-separated YYYY-MM-DD values."
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(field.required)}
                  onChange={(e) => update({ required: e.target.checked })}
                />
              }
              label={
                field.mode === "range" ? "Start date required" : "Required"
              }
            />
            {field.mode === "range" && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(field.endRequired)}
                    onChange={(e) => update({ endRequired: e.target.checked })}
                  />
                }
                label="End date required"
              />
            )}
          </Stack>
        )}

        {field.type === "EmbeddedLink" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Link text"
              value={field.text}
              inputProps={{ maxLength: 25 }}
              onChange={(e) => update({ text: e.target.value })}
              fullWidth
            />
            {renderActionEditor(field, [
              { value: "open_url", label: "Open URL" },
              { value: "navigate", label: "Navigate" },
              { value: "data_exchange", label: "Data exchange" },
            ])}
          </Stack>
        )}

        {field.type === "Footer" && (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Button label"
              value={field.label}
              inputProps={{ maxLength: 35 }}
              onChange={(e) => update({ label: e.target.value })}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                label="Left caption"
                value={field.leftCaption}
                inputProps={{ maxLength: 15 }}
                onChange={(e) => update({ leftCaption: e.target.value })}
                fullWidth
                disabled={Boolean(field.centerCaption)}
              />
              <TextField
                size="small"
                label="Right caption"
                value={field.rightCaption}
                inputProps={{ maxLength: 15 }}
                onChange={(e) => update({ rightCaption: e.target.value })}
                fullWidth
                disabled={Boolean(field.centerCaption)}
              />
            </Stack>
            <TextField
              size="small"
              label="Center caption"
              value={field.centerCaption}
              inputProps={{ maxLength: 15 }}
              onChange={(e) => update({ centerCaption: e.target.value })}
              fullWidth
              disabled={Boolean(field.leftCaption || field.rightCaption)}
            />
            <SectionLabel>Button action</SectionLabel>
            {renderActionEditor(field, [
              { value: "auto", label: "Auto: next screen / complete" },
              { value: "navigate", label: "Navigate to a screen" },
              { value: "complete", label: "Complete Flow" },
              { value: "data_exchange", label: "Data exchange" },
            ])}
            {field.action?.name === "auto" && (
              <Alert severity="info">
                Auto mode navigates to the next screen. On the final screen it
                completes the Flow and includes all named fields in the
                completion payload.
              </Alert>
            )}
          </Stack>
        )}

        {renderCommonStateControls(field)}
      </Box>
    );
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 2,
        px: { xs: 1.5, sm: 2, xl: 3 },
        bgcolor: "#f6f7f9",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ mb: 1 }}>
        <BackBtn />
      </Box>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          position: { lg: "sticky" },
          top: { lg: 8 },
          zIndex: 1100,
          px: { xs: 1.5, md: 2 },
          py: 1.5,
          mb: 2,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <SmartphoneIcon fontSize="small" />
              </Box>

              <Box minWidth={0}>
                <PageTitle>WhatsApp Flow Builder</PageTitle>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ maxWidth: { xs: 260, md: 520 } }}
                >
                  {data?.detail?.name || "Flow"} · {activeScreen.title}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              mt={1}
            >
              <Chip size="small" label={`JSON ${FLOW_JSON_VERSION}`} />
              <Chip
                size="small"
                variant="outlined"
                label={`${screens.length} screen${
                  screens.length === 1 ? "" : "s"
                }`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${generatedSizeKb.toFixed(1)} KB`}
              />
              {validation.errors.length ? (
                <Chip
                  size="small"
                  color="error"
                  icon={<WarningAmberOutlinedIcon />}
                  label={`${validation.errors.length} error${
                    validation.errors.length === 1 ? "" : "s"
                  }`}
                  onClick={() => setValidationDialogOpen(true)}
                  clickable
                />
              ) : (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleOutlineIcon />}
                  label="Valid"
                  onClick={() => setValidationDialogOpen(true)}
                  clickable
                />
              )}
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: "flex-start", lg: "flex-end" }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddScreen}
            >
              New screen
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => setJsonDialogOpen(true)}
            >
              View JSON
            </Button>

            <LoadingButton
              size="small"
              variant="contained"
              loading={savingDraft}
              onClick={handleSaveDraft}
              startIcon={<SaveIcon />}
              sx={{ px: 2 }}
            >
              Save draft
            </LoadingButton>
          </Stack>
        </Stack>
      </Paper>

      {validation.warnings.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button size="small" onClick={() => setValidationDialogOpen(true)}>
              View all
            </Button>
          }
        >
          {validation.warnings[0]}
        </Alert>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "260px minmax(480px, 1fr) 360px",
              xl: "280px minmax(560px, 1fr) 390px",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          {/* LEFT: SCREENS + COMPONENTS */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              position: { lg: "sticky" },
              top: { lg: 116 },
              maxHeight: { lg: "calc(100vh - 132px)" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LayersOutlinedIcon fontSize="small" />
                  <Typography fontWeight={700}>Screens</Typography>
                </Stack>

                <Tooltip title="Add screen">
                  <IconButton size="small" onClick={openAddScreen}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack spacing={0.75} mt={1.25}>
                {screens.map((screen, index) => {
                  const active = index === currentScreen;
                  const screenErrors = validation.errors.filter((item) =>
                    item.includes(`Screen ${index + 1}`)
                  ).length;

                  return (
                    <Button
                      key={screen.editorId}
                      onClick={() => {
                        setCurrentScreen(index);
                        setSelectedFieldId(null);
                        setIsDrawerOpen(false);
                      }}
                      fullWidth
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        px: 1,
                        py: 0.85,
                        borderRadius: 2,
                        color: active ? "primary.main" : "text.primary",
                        bgcolor: active ? "action.selected" : "transparent",
                        border: "1px solid",
                        borderColor: active ? "primary.main" : "transparent",
                        "&:hover": {
                          bgcolor: active ? "action.selected" : "action.hover",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          mr: 1,
                          flexShrink: 0,
                          bgcolor: active ? "primary.main" : "action.hover",
                          color: active
                            ? "primary.contrastText"
                            : "text.secondary",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ minWidth: 0, textAlign: "left", flex: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {screen.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {screen.id}
                        </Typography>
                      </Box>

                      {screenErrors > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          label={screenErrors}
                          sx={{ height: 20, ml: 0.5 }}
                        />
                      )}
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            <Box sx={{ p: 1.5, overflowY: "auto" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
                <AddCircleOutlineIcon fontSize="small" />
                <Typography fontWeight={700}>Components</Typography>
              </Stack>

              <TextField
                size="small"
                value={componentSearch}
                onChange={(e) => setComponentSearch(e.target.value)}
                placeholder="Search components"
                fullWidth
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Droppable droppableId="availableFields" isDropDisabled>
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {componentCategories.map((category) => (
                      <Box key={category} sx={{ mb: 1.5 }}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: 0.8,
                          }}
                        >
                          {category}
                        </Typography>

                        <Stack spacing={0.65} mt={0.35}>
                          {filteredAvailableFields
                            .filter((field) => field.category === category)
                            .map((field) => {
                              const sourceIndex = AVAILABLE_FIELDS.findIndex(
                                (item) => item.id === field.id
                              );

                              return (
                                <Draggable
                                  key={field.id}
                                  draggableId={field.id}
                                  index={sourceIndex}
                                >
                                  {(dragProvided) => (
                                    <Paper
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      variant="outlined"
                                      sx={{
                                        px: 1,
                                        py: 0.85,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        cursor: "grab",
                                        borderRadius: 2,
                                        transition: "all .15s ease",
                                        "&:hover": {
                                          borderColor: "primary.main",
                                          bgcolor: "action.hover",
                                          transform: "translateY(-1px)",
                                        },
                                      }}
                                      style={dragProvided.draggableProps.style}
                                    >
                                      <Box
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          borderRadius: 1.5,
                                          display: "grid",
                                          placeItems: "center",
                                          bgcolor: "action.hover",
                                          color: "text.secondary",
                                          flexShrink: 0,
                                        }}
                                      >
                                        {field.icon}
                                      </Box>

                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ flex: 1, minWidth: 0 }}
                                        noWrap
                                      >
                                        {field.label}
                                      </Typography>

                                      <Tooltip title="Add to screen">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            addFieldFromPalette(field);
                                          }}
                                          onMouseDown={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Paper>
                                  )}
                                </Draggable>
                              );
                            })}
                        </Stack>
                      </Box>
                    ))}

                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>

              {filteredAvailableFields.length === 0 && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No components match “{componentSearch}”.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* CENTER: PHONE CANVAS */}
          <Box sx={{ minWidth: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                minHeight: 720,
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, md: 2 },
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box minWidth={0}>
                    <Typography fontWeight={800}>
                      {activeScreen.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activeScreen.id} · {formFields.length}/50 components ·{" "}
                      {currentScreen === screens.length - 1
                        ? "Terminal screen"
                        : "Navigation screen"}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Tooltip title="Screen settings">
                      <IconButton size="small" onClick={openEditScreen}>
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Duplicate screen">
                      <IconButton size="small" onClick={duplicateScreen}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Move earlier">
                      <span>
                        <IconButton
                          size="small"
                          disabled={currentScreen === 0}
                          onClick={() => moveScreen(-1)}
                        >
                          <Typography fontWeight={800}>↑</Typography>
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Move later">
                      <span>
                        <IconButton
                          size="small"
                          disabled={currentScreen === screens.length - 1}
                          onClick={() => moveScreen(1)}
                        >
                          <Typography fontWeight={800}>↓</Typography>
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete screen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={deleteScreen}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  minHeight: 660,
                  px: { xs: 1, sm: 2 },
                  py: 3,
                  bgcolor: "#eef1f4",
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(0,0,0,.08) 1px, transparent 0)",
                  backgroundSize: "18px 18px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 430,
                    border: "9px solid #202124",
                    borderRadius: "36px",
                    bgcolor: "#fff",
                    boxShadow: "0 18px 50px rgba(17,24,39,.18)",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      height: 24,
                      bgcolor: "#202124",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.625,
                        width: 72,
                        height: 5,
                        borderRadius: 9,
                        bgcolor: "#5f6368",
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fff",
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          bgcolor: "#e9f8ef",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 800,
                          fontSize: 13,
                          color: "#137333",
                        }}
                      >
                        WA
                      </Box>

                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={800} noWrap>
                          {data?.detail?.name || "WhatsApp Flow"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activeScreen.title}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Droppable droppableId="formFields">
                    {(provided, dropSnapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 560,
                          maxHeight: "calc(100vh - 285px)",
                          overflowY: "auto",
                          px: 1.5,
                          py: 1.75,
                          bgcolor: dropSnapshot.isDraggingOver
                            ? "#f8fbff"
                            : "#fff",
                          transition: "background-color .15s ease",
                        }}
                      >
                        {formFields.length === 0 && (
                          <Box
                            sx={{
                              minHeight: 440,
                              border: "1.5px dashed",
                              borderColor: "divider",
                              borderRadius: 2.5,
                              display: "grid",
                              placeItems: "center",
                              textAlign: "center",
                              px: 3,
                            }}
                          >
                            <Box>
                              <AddCircleOutlineIcon
                                color="disabled"
                                sx={{ fontSize: 42 }}
                              />
                              <Typography fontWeight={700} mt={1}>
                                Build this screen
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mt={0.5}
                              >
                                Drag a component here or click + in the
                                component list.
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {formFields.map((field, index) => (
                          <Draggable
                            key={field.editorId}
                            draggableId={field.editorId}
                            index={index}
                          >
                            {(dragProvided, snapshot) => {
                              const selected =
                                selectedFieldId === field.editorId;

                              return (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  sx={{ mb: 0.85 }}
                                  style={dragProvided.draggableProps.style}
                                >
                                  <Paper
                                    elevation={0}
                                    onClick={() => {
                                      setSelectedFieldId(field.editorId);
                                      setBulkOptions("");
                                    }}
                                    sx={{
                                      position: "relative",
                                      p: 1.15,
                                      borderRadius: 2,
                                      cursor: "default",
                                      border: "1px solid",
                                      borderColor: selected
                                        ? "primary.main"
                                        : snapshot.isDragging
                                        ? "primary.light"
                                        : "transparent",
                                      bgcolor: selected
                                        ? "rgba(25,118,210,.035)"
                                        : snapshot.isDragging
                                        ? "action.hover"
                                        : "transparent",
                                      boxShadow: snapshot.isDragging
                                        ? "0 8px 24px rgba(0,0,0,.12)"
                                        : "none",
                                      transition:
                                        "border-color .15s ease, background .15s ease",
                                      "&:hover": {
                                        borderColor: selected
                                          ? "primary.main"
                                          : "divider",
                                        "& .flow-field-actions": {
                                          opacity: 1,
                                        },
                                        "& .flow-drag-handle": {
                                          opacity: 1,
                                        },
                                      },
                                    }}
                                  >
                                    <Box
                                      className="flow-drag-handle"
                                      {...dragProvided.dragHandleProps}
                                      sx={{
                                        position: "absolute",
                                        left: -11,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        width: 22,
                                        height: 34,
                                        display: "grid",
                                        placeItems: "center",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        color: "text.secondary",
                                        cursor: "grab",
                                        opacity: selected ? 1 : 0,
                                        transition: "opacity .15s ease",
                                        zIndex: 2,
                                      }}
                                    >
                                      <DragIndicatorIcon
                                        sx={{ fontSize: 17 }}
                                      />
                                    </Box>

                                    <Box>{renderFormElement(field)}</Box>

                                    <Stack
                                      className="flow-field-actions"
                                      direction="row"
                                      spacing={0.25}
                                      sx={{
                                        position: "absolute",
                                        top: 5,
                                        right: 5,
                                        opacity: selected ? 1 : 0,
                                        transition: "opacity .15s ease",
                                        bgcolor: "rgba(255,255,255,.92)",
                                        borderRadius: 1.5,
                                        p: 0.25,
                                      }}
                                    >
                                      <Tooltip title="Edit properties">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFieldEdit(field.editorId);
                                          }}
                                        >
                                          <EditIcon sx={{ fontSize: 17 }} />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Duplicate">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            duplicateField(field.editorId);
                                          }}
                                        >
                                          <ContentCopyIcon
                                            sx={{ fontSize: 17 }}
                                          />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFieldDelete(field.editorId);
                                          }}
                                        >
                                          <DeleteOutlineIcon
                                            sx={{ fontSize: 17 }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>

                                    {selected && (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        flexWrap="wrap"
                                        useFlexGap
                                        mt={1}
                                      >
                                        <Chip
                                          size="small"
                                          label={field.subType || field.type}
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: 10 }}
                                        />
                                        {field.name && (
                                          <Chip
                                            size="small"
                                            label={field.name}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: 10 }}
                                          />
                                        )}
                                        {field.required && (
                                          <Chip
                                            size="small"
                                            label="required"
                                            sx={{ height: 20, fontSize: 10 }}
                                          />
                                        )}
                                      </Stack>
                                    )}
                                  </Paper>
                                </Box>
                              );
                            }}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>

                  <Box
                    sx={{
                      height: 24,
                      bgcolor: "#fff",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 110,
                        height: 4,
                        borderRadius: 4,
                        bgcolor: "#d8dadd",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* RIGHT: PROPERTIES */}
          <Paper
            variant="outlined"
            sx={{
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              position: "sticky",
              top: 116,
              height: "calc(100vh - 132px)",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <TuneOutlinedIcon fontSize="small" />
                <Box minWidth={0}>
                  <Typography fontWeight={800}>Properties</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedField
                      ? selectedField.subType || selectedField.type
                      : "Select a component"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {selectedField ? (
                renderEditingPanel()
              ) : (
                <Box
                  sx={{
                    minHeight: 320,
                    px: 3,
                    display: "grid",
                    placeItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Box>
                    <TuneOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
                    <Typography fontWeight={700} mt={1}>
                      Nothing selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Click a component in the phone preview to edit its label,
                      field key, validation, options, actions and visibility.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </DragDropContext>

      {/* Properties drawer for smaller screens */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          display: { xs: "block", lg: "none" },
          zIndex: 1201,
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 560 },
            maxWidth: "100vw",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.25,
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="h6">Component properties</Typography>
            {selectedField && (
              <Typography variant="caption" color="text.secondary">
                {selectedField.subType || selectedField.type}
              </Typography>
            )}
          </Box>

          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: "auto", flex: 1 }}>{renderEditingPanel()}</Box>
      </Drawer>

      <Dialog
        open={screenDialog.open}
        onClose={closeScreenDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {screenDialog.mode === "add" ? "Add Screen" : "Screen Settings"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Screen title"
              value={screenDialog.title}
              onChange={(e) =>
                setScreenDialog((prev) => ({ ...prev, title: e.target.value }))
              }
              fullWidth
              autoFocus
            />
            <TextField
              label="Screen ID"
              value={screenDialog.id}
              onChange={(e) =>
                setScreenDialog((prev) => ({
                  ...prev,
                  id: sanitizeScreenId(e.target.value),
                }))
              }
              fullWidth
              helperText="Stable ID used by navigation and cross-screen references."
            />
            <TextField
              label="Screen data schema JSON"
              value={screenDialog.dataText}
              onChange={(e) =>
                setScreenDialog((prev) => ({
                  ...prev,
                  dataText: e.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={8}
              error={!isValidJsonObject(screenDialog.dataText || "{}")}
              helperText="Keep {} for a purely static screen. Use this for ${data.*} bindings or endpoint/navigate data."
              sx={{
                "& textarea": {
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 12,
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={screenDialog.refreshOnBack}
                  onChange={(e) =>
                    setScreenDialog((prev) => ({
                      ...prev,
                      refreshOnBack: e.target.checked,
                    }))
                  }
                />
              }
              label="Refresh screen data when user navigates back"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScreenDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveScreenDialog}>
            {screenDialog.mode === "add" ? "Add Screen" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              Generated Flow JSON
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                {generatedSizeKb.toFixed(1)} KB · version {FLOW_JSON_VERSION}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={copyJson}
              >
                Copy
              </Button>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadJson}
              >
                Download
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            value={generatedJsonText}
            fullWidth
            multiline
            minRows={24}
            InputProps={{ readOnly: true }}
            sx={{
              mt: 1,
              "& textarea": {
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 12,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Flow Validation</DialogTitle>
        <DialogContent>
          {validation.errors.length === 0 &&
            validation.warnings.length === 0 && (
              <Alert severity="success">
                No builder validation issues found.
              </Alert>
            )}

          {validation.errors.length > 0 && (
            <Box mb={2}>
              <Alert severity="error" sx={{ mb: 1 }}>
                Fix these before saving.
              </Alert>
              <Stack spacing={0.75}>
                {validation.errors.map((message, index) => (
                  <Typography key={`${message}-${index}`} variant="body2">
                    {index + 1}. {message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {validation.warnings.length > 0 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                These do not block saving but are worth reviewing.
              </Alert>
              <Stack spacing={0.75}>
                {validation.warnings.map((message, index) => (
                  <Typography key={`${message}-${index}`} variant="body2">
                    {index + 1}. {message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FlowForms;
